/**
 * Controlled amenity vocabulary (issue #953) — a typed COPY of
 * `shared/src/amenities.ts`, where the list is defined once (the onboarding
 * file validates apartment amenities against it). The admin UI does not build
 * from `@kolula/shared` (docs/plans/shared-rules-package.md, open question 2),
 * so `amenities.test.ts` imports the shared list and fails when this copy
 * differs. Change both in one PR.
 *
 * Amenities are identified by a stable `key` and rendered via i18n at display
 * time (`t('amenities.' + key)`), so a customer always sees the label in their
 * own language regardless of the tenant's language. This replaces the previous
 * freeform amenity text input.
 *
 * `category` is a soft grouping used only to organise the picker into sections
 * (universal / venue / hall / apartment / rental) — it is NOT a hard filter, so every
 * section is shown regardless of the asset's use case. `icon` is a stable
 * (lucide-style) name kept for future use; labels live in the i18n files under
 * `amenities.*` in both admin-ui and apps/end-user.
 *
 * NOTE: this list is reviewed with product before finalising — keep it flat and
 * easy to scan. Adding an entry means adding its `amenities.<key>` label to all
 * four i18n files (admin-ui + end-user, en + de).
 */

export type AmenityCategory = 'universal' | 'venue' | 'hall' | 'apartment' | 'rental';

export interface AmenityDefinition {
    /** Stable canonical identifier, e.g. `wifi`. Stored as `amenity.key`. */
    key: string;
    /** Stable (lucide-style) icon name. Stored for future use, not yet rendered. */
    icon: string;
    /** Soft grouping for the picker sections — not a hard filter. */
    category: AmenityCategory;
}

export const AMENITY_CATEGORY_ORDER: AmenityCategory[] = [
    'universal', 'venue', 'hall', 'apartment', 'rental',
];

export const AMENITIES: readonly AmenityDefinition[] = [
    // Universal — relevant across venues, apartments and rentals.
    { key: 'wifi', icon: 'wifi', category: 'universal' },
    { key: 'parking', icon: 'square-parking', category: 'universal' },
    { key: 'restrooms', icon: 'toilet', category: 'universal' },
    { key: 'heating', icon: 'thermometer', category: 'universal' },
    { key: 'air_conditioning', icon: 'snowflake', category: 'universal' },
    { key: 'elevator', icon: 'arrow-up-down', category: 'universal' },
    { key: 'wheelchair_accessible', icon: 'accessibility', category: 'universal' },
    { key: 'smoking_allowed', icon: 'cigarette', category: 'universal' },
    { key: 'pets_allowed', icon: 'paw-print', category: 'universal' },
    { key: 'coffee_machine', icon: 'coffee', category: 'universal' },
    { key: 'kitchen', icon: 'utensils', category: 'universal' },

    // Venue — meeting / event spaces.
    { key: 'projector', icon: 'presentation', category: 'venue' },
    { key: 'sound_system', icon: 'speaker', category: 'venue' },
    { key: 'microphone', icon: 'mic', category: 'venue' },
    { key: 'whiteboard', icon: 'presentation', category: 'venue' },
    { key: 'flipchart', icon: 'presentation', category: 'venue' },
    { key: 'stage', icon: 'theater', category: 'venue' },
    { key: 'chairs', icon: 'armchair', category: 'venue' },
    { key: 'tables', icon: 'table', category: 'venue' },
    { key: 'kitchenette', icon: 'cooking-pot', category: 'venue' },
    { key: 'catering_on_request', icon: 'utensils-crossed', category: 'venue' },
    { key: 'lectern', icon: 'presentation', category: 'venue' },
    { key: 'streaming_camera', icon: 'video', category: 'venue' },

    // Sports hall — the venue list above plus what a hall adds (PO, 2026-10-18).
    { key: 'changing_rooms', icon: 'shirt', category: 'hall' },
    { key: 'showers', icon: 'shower-head', category: 'hall' },
    { key: 'spectator_stand', icon: 'users', category: 'hall' },
    { key: 'club_room', icon: 'door-open', category: 'hall' },

    // Apartment — self-catering stays.
    { key: 'tv', icon: 'tv', category: 'apartment' },
    { key: 'dishwasher', icon: 'utensils', category: 'apartment' },
    { key: 'washing_machine', icon: 'washing-machine', category: 'apartment' },
    { key: 'dryer', icon: 'wind', category: 'apartment' },
    { key: 'hair_dryer', icon: 'wind', category: 'apartment' },
    { key: 'iron', icon: 'shirt', category: 'apartment' },
    { key: 'workspace', icon: 'laptop', category: 'apartment' },
    { key: 'crib', icon: 'baby', category: 'apartment' },
    { key: 'balcony', icon: 'door-open', category: 'apartment' },
    { key: 'garden_access', icon: 'trees', category: 'apartment' },

    // Rental — item rentals shipped with accessories.
    { key: 'helmet_included', icon: 'hard-hat', category: 'rental' },
    { key: 'lock_included', icon: 'lock', category: 'rental' },
    { key: 'charger_included', icon: 'battery-charging', category: 'rental' },
    { key: 'manual_included', icon: 'book-open', category: 'rental' },
] as const;

/** Fast membership test to distinguish vocabulary keys from legacy freeform data. */
export const AMENITY_KEYS: ReadonlySet<string> = new Set(AMENITIES.map((a) => a.key));

/** `type` marker stored on vocabulary picks. Non-`Text` so the end-user app renders it as a bullet. */
export const AMENITY_TYPE = 'amenity';
